/**
 * How urgent an attention item is: the Figma red / yellow / green treatment,
 * plus `info` for notifications that ask nothing of the user. Callers map
 * their own deadline rules onto these levels.
 */
export type TaskUrgency = 'urgent' | 'soon' | 'onTrack' | 'info';

/**
 * Orders attention items nearest deadline first, as the Figma spec requires
 * ("nearest deadline always appears at the top"). Items without a deadline
 * keep their relative order after every dated item. Does not mutate the input.
 *
 * @param dueAt reads an item's deadline as an ISO 8601 date or datetime; return undefined when it has none
 */
export function sortByDeadline<T>(
  items: readonly T[],
  dueAt: (item: T) => string | undefined
): T[] {
  const time = (item: T) => {
    const value = dueAt(item);
    const parsed = value ? Date.parse(value) : NaN;
    return Number.isNaN(parsed) ? Infinity : parsed;
  };
  return items
    .map((item, index) => ({ item, index, at: time(item) }))
    .sort((a, b) => a.at - b.at || a.index - b.index)
    .map(({ item }) => item);
}
